import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, RefreshCw, Coins, CalendarDays, FileSignature, MessageSquare, Pencil, Users, Check, Paperclip, FileText, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader, SpecPill, Pill, StatusPill } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const Fact = ({ icon: Icon, label, value }) => value ? (
  <div className="panel p-4">
    <p className="eyebrow flex items-center gap-1.5"><Icon size={12} />{label}</p>
    <p className="mt-1.5 font-semibold text-slate-900">{value}</p>
  </div>
) : null;

function ApplyDialog({ job, open, onOpenChange, onDone }) {
  const api = useApi();
  const [msg, setMsg] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (list) => {
    const picked = Array.from(list || []).slice(0, 5 - files.length);
    if (!picked.length) return;
    setBusy(true);
    try {
      const done = [];
      for (const f of picked) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: file larger than 10 MB`); continue; }
        const fd = new FormData(); fd.append("file", f);
        done.push(await api("post", `/jobs/${job.id}/attachments`, fd));
      }
      setFiles((prev) => [...prev, ...done]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); fileRef.current.value = ""; }
  };
  const submit = async () => {
    setBusy(true);
    try {
      onDone(await api("post", `/jobs/${job.id}/apply`, { message: msg, attachments: files.map((f) => f.path) }));
      toast.success("Application sent with your profile"); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle>Apply with profile</DialogTitle>
          <DialogDescription>{job.company_name} will see your profile, certificates and CV. Your message and files also go to the messenger.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="apply-message-input" rows={5} placeholder="Short message to the company (optional)" value={msg} onChange={(e) => setMsg(e.target.value)} />
        <div className="space-y-2">
          <input ref={fileRef} type="file" multiple hidden accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => upload(e.target.files)} data-testid="apply-file-input" />
          <Button type="button" variant="outline" size="sm" disabled={busy || files.length >= 5} onClick={() => fileRef.current.click()} data-testid="apply-attach-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}Attach photos or PDF ({files.length}/5)
          </Button>
          {files.map((f, i) => (
            <div key={f.path} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-1.5" data-testid={`apply-file-${i}`}>
              <FileText size={14} className="text-slate-500" /><span className="flex-1 truncate">{f.filename}</span>
              <button type="button" onClick={() => setFiles(files.filter((x) => x.path !== f.path))} data-testid={`apply-file-remove-${i}`} aria-label="Remove"><X size={14} /></button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button data-testid="apply-confirm-button" onClick={submit} disabled={busy}>{busy ? "Sending..." : "Send application"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const { me } = useMe();
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { api("get", `/jobs/${id}`).then(setJob).catch(() => { toast.error("Job not found"); nav("/jobs"); }); }, [id, api, nav]);
  const place = useMemo(() => [job?.location, job?.country].filter(Boolean).join(", "), [job?.location, job?.country]);
  if (!job) return <Loader />;
  const isOwner = job.company_id === me.user_id;

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav(-1)} data-testid="job-back-button" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={16} />Back</button>
      <div className="mt-6 rise">
        <Link to={`/u/${job.company_id}`} data-testid="job-company-link" className="eyebrow hover:text-sky-700">{job.company_name}</Link>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="job-title">{job.title}</h1>
        <div className="mt-4 flex flex-wrap gap-1.5">
          <SpecPill spec={job.specialization} />
          <Pill>{job.site_type}</Pill>
          {!job.active && <Pill cls="bg-red-50 text-red-700 border-red-200">Inactive</Pill>}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
        <Fact icon={MapPin} label="Location" value={place} />
        <Fact icon={RefreshCw} label="Rotation" value={job.rotation} />
        <Fact icon={Coins} label="Rate" value={job.rate} />
        <Fact icon={FileSignature} label="Contract" value={job.contract} />
        <Fact icon={CalendarDays} label="Start" value={job.start_date} />
      </div>
      <div className="mt-8 panel p-6">
        <h2 className="font-bold text-lg">Description</h2>
        <p className="mt-3 text-slate-700 whitespace-pre-line leading-relaxed">{job.description || "No description."}</p>
        {job.requirements?.length > 0 && (
          <>
            <h2 className="mt-6 font-bold text-lg">Requirements</h2>
            <ul className="mt-3 space-y-2">
              {job.requirements.map((r) => <li key={r} className="flex gap-2 text-slate-700"><Check size={18} className="text-sky-600 shrink-0" />{r}</li>)}
            </ul>
          </>
        )}
      </div>
      <div className="mt-6 flex flex-wrap gap-3 sticky bottom-16 md:bottom-0 bg-slate-50/95 backdrop-blur py-3 -mx-1 px-1">
        {isOwner ? (
          <>
            <Button asChild data-testid="job-edit-button"><Link to={`/jobs/${job.id}/edit`}><Pencil size={16} />Edit</Link></Button>
            <Button asChild variant="outline" className="bg-white" data-testid="job-applicants-button"><Link to={`/my-jobs/${job.id}/applicants`}><Users size={16} />Candidates</Link></Button>
            <Button asChild variant="outline" className="bg-white" data-testid="job-find-nearby-button">
              <Link to={`/people?role=technician&specialization=${job.specialization}&near=${encodeURIComponent([job.location, job.country].filter(Boolean).join(", "))}&radius=250`}><MapPin size={16} />Find technicians nearby</Link>
            </Button>
          </>
        ) : (
          <>
            {me.role === "technician" && (job.application ? (
              <div className="flex items-center gap-2 panel px-4 py-2" data-testid="job-applied-status">Applied · <StatusPill status={job.application.status} /></div>
            ) : (
              <Button size="lg" data-testid="job-card-apply-button" onClick={() => setOpen(true)} className="shadow-lg">Apply with profile</Button>
            ))}
            <Button size="lg" variant="outline" className="bg-white" data-testid="job-message-company-button" onClick={() => nav(`/messages/${job.company_id}`)}>
              <MessageSquare size={16} />Message company
            </Button>
          </>
        )}
      </div>
      <ApplyDialog job={job} open={open} onOpenChange={setOpen} onDone={(a) => setJob({ ...job, application: a })} />
    </div>
  );
}
